import { useEffect, useState } from "react";

import {
  TextField,
  Typography,
  Select,
  Checkbox,
  DocumentList,
  DocumentsModal,
  Button,
  TextEditor,
} from "@king-ict/eupisi-ui/components";
import { CodeBook, UploadFile } from "@king-ict/eupisi-ui/models";
import { codeBookToSelectData, nameof } from "@king-ict/eupisi-ui/utils";
import { Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

import {
  getGroupList,
  getItemById,
  saveItem,
  getFaqDocumentById,
} from "../../api/Faq/FaqApi";
import { useAppDispatch } from "../../app/store";
import {
  ContentArea,
  FormCard,
  ToolbarEdit,
  ToolbarTop,
} from "../../components";
import { ValidationFailure } from "../../models";
import { Item } from "../../models/Faq";
import { Attachment } from "../../models/Shared/Attachment";
import { faqItemDetailUrl } from "../../routes/routes";
import { resetIsEdited, setIsEdited } from "../../slices/applicationSlice";

export function FaqItemEdit() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const createDefaultItem = (): Item => {
    return {
      id: 0,
      group: { id: 0, name: "" },
      status: true,
    };
  };
  const [item, setItem] = useState<Item>(createDefaultItem());
  const [groups, setGroups] = useState<CodeBook[]>();
  const [errors, setErrors] = useState<ValidationFailure[]>();
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

  useEffect(() => {
    (async () => {
      var groupList = await getGroupList();
      setGroups(groupList);
      if (!isNaN(+id) && +id > 0) {
        var result = await getItemById(+id);
        if (result) {
          setItem(result);
        }
      }
    })();
    dispatch(resetIsEdited());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleGroupChange = (e: any) => {
    const groupId = Number.parseInt(e.target.value);
    const newItem: Item = {
      ...item,
      group: { id: groupId, name: "" },
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleStatusChange = () => {
    const status = item.status;
    const newItem: Item = {
      ...item,
      status: !status,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleQuestionChange = (e: any) => {
    const question = e.target.value;
    const newItem: Item = {
      ...item,
      question: question,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleAnswerChange = (val: string) => {
    const newItem: Item = {
      ...item,
      answer: val,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleFileChange = async (files: File[]) => {
    var uploadedFiles: UploadFile[] = [];
    if (files && files.length > 0) {
      files.forEach((file) => {
        const fileReader = new FileReader();
        fileReader.readAsBinaryString(file);
        var uploadedFile = {
          fileName: file.name,
          fileData: fileReader.result,
        } as UploadFile;
        uploadedFiles.push(uploadedFile);
      });
    }
    const newItem: Item = {
      ...item,
      uploadFiles: uploadedFiles.length > 0 ? uploadedFiles : undefined,
    };
    setItem(newItem);
    dispatch(setIsEdited());
  };
  const handleUploadFileChange = (files: UploadFile[]) => {
    const newItem: Item = {
      ...item,
      uploadFiles: files ?? undefined,
    };
    setItem(newItem);
    dispatch(setIsEdited());
    setShowUploadModal(false);
  };
  const handleGetDocument = (doc: Attachment) => {
    if (doc && doc.id) {
      getFaqDocumentById(doc.id);
    }
  };
  const handleRemoveDocument = (doc: Attachment) => {
    if (doc && doc.id) {
      const newItem: Item = {
        ...item,
        attachments: item.attachments?.filter((val) => val.id !== doc.id),
      };
      setItem(newItem);
      dispatch(setIsEdited());
    }
  };

  if (!item.answer && id !== "0") return null;
  return (
    <ContentArea>
      <ToolbarTop
        showBack="show"
        pageTitle={t("navigation.faqList")}
        errors={errors}
      ></ToolbarTop>
      <FormCard>
        <Row>
          <Col md={12}>
            <Select
              label={t("common.faq.group")}
              name={"item.groupId"}
              value={item.group?.id}
              onChange={handleGroupChange}
              selectData={codeBookToSelectData(groups)}
              errorText={
                errors?.find((e) => e.propertyName === "GroupId")?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextField
              label={t("common.faq.question")}
              name={"item.question"}
              value={item.question}
              onChange={handleQuestionChange}
              errorText={
                errors?.find((e) => e.propertyName === "Question")?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Typography variant="medium" component="div" color="light">
              {t("common.faq.answer")}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <TextEditor
              label={t("common.announcement.text")}
              initialValue={item.answer}
              handleChange={handleAnswerChange}
              errorText={
                errors?.find((e) => e.propertyName === "Text")?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Checkbox
              label={t("common.faq.active")}
              name={nameof<typeof item>("status")}
              checked={item.status}
              onChange={handleStatusChange}
              errorText={
                errors?.find((e) => e.propertyName === "Status")?.message
              }
            />
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <Typography variant="medium" component="div" color="light">
              {t("common.announcement.attachments")}
            </Typography>
          </Col>
        </Row>
        <Row>
          <Col md={4}>
            <DocumentList
              documents={item.attachments ?? []}
              onDocumentClick={handleGetDocument}
              onRemoveClick={handleRemoveDocument}
            />
          </Col>
          <Col md={4}>
            <Button
              color="primary"
              onClick={() => setShowUploadModal(true)}
              size="sm"
            >
              {t("accessibility.documentModal.uploadFile")}
            </Button>
          </Col>
          <Col md={4}>
            <DocumentList
              documents={item.uploadFiles ?? []}
              removable={false}
            />
          </Col>
        </Row>
      </FormCard>
      <ToolbarEdit
        showSave={"show"}
        showCancel="show"
        redirectUrlSaveOK={faqItemDetailUrl}
        handleSave={async () => saveItem(item)}
        setSaveErrors={setErrors}
      ></ToolbarEdit>
      <DocumentsModal
        title={t("accessibility.documentModal.title")}
        infoPanelText={t("accessibility.documentModal.info")}
        types={t("accessibility.documentModal.types")}
        allDocuments={item.uploadFiles ?? []}
        maxSize={10}
        filesAction={handleFileChange}
        open={showUploadModal}
        onConfirm={handleUploadFileChange}
        onClose={() => setShowUploadModal(false)}
        hideOldDocuments={true}
        btnCancelText={t("common.quit")}
        btnOkText={t("common.addNew")}
      />
    </ContentArea>
  );
}
